import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import FilterDropdown from '../../../components/common/dropdowns/FilterDropdown/FilterDropdown';
import FilterChip from '../../../components/common/dropdowns/FilterDropdown/components/FilterChip';
import FilterIcon from '../../../components/icons/FilterIcon';
import { colors } from '../../../styles/variables';
import { DictionaryDropdownOption, Tag } from '../../../types/ApiTypes';
import { DropdownOption } from '../../../types/Dropdown.types';

interface TemplatesViewFiltersProps {
  isGlobalAdmin: boolean;
  tags: Tag[];
  tagFilters: string[];
  setTagFilters: (newTags: string[]) => void;
  gameCharactersOptions: DictionaryDropdownOption[];
  gameCharacterFilter: string;
  setGameCharacterFilter: (newValue: string) => void;
  citiesOptions: string[];
  cityFilter: string;
  setCityFilter: (newValue: string) => void;
  ownersOptions: {
    id: string;
    email: string;
  }[];
  ownerFilter: string;
  setOwnerFilter: (newValue: string) => void;
  gameTypes: DictionaryDropdownOption[] | undefined;
  gameTypeFilter: string;
  setGameTypeFilter: (newValue: string) => void;
}

const TemplatesViewFilters = ({
  isGlobalAdmin,
  tags,
  tagFilters,
  setTagFilters,
  gameCharactersOptions,
  gameCharacterFilter,
  setGameCharacterFilter,
  citiesOptions,
  cityFilter,
  setCityFilter,
  ownersOptions,
  ownerFilter,
  setOwnerFilter,
  gameTypes,
  gameTypeFilter,
  setGameTypeFilter,
}: TemplatesViewFiltersProps) => {
  const { t } = useTranslation();

  const gameCharacterLabel = gameCharactersOptions.find(option => option.code === gameCharacterFilter)?.value ?? '';

  const gameTypeLabel = gameTypes?.find(gameType => gameType.code === gameTypeFilter)?.value ?? '';

  const handleChangeTagFilters = (option: DropdownOption | null) => {
    if (!option?.value) {
      return;
    }

    const isExisting = tagFilters.some(tagItem => tagItem === option.value);

    if (isExisting) {
      const remainingTagString = tagFilters.filter(item => item !== option.value);
      setTagFilters(remainingTagString);
    } else {
      const newTagString = [...tagFilters, option.value];
      setTagFilters(newTagString);
    }
  };

  const handleDeleteTagFilter = (tagToRemove: string) => {
    const remainingTagString = tagFilters.filter(item => item !== tagToRemove);
    setTagFilters(remainingTagString);
  };

  return (
    <div className="flex gap-4">
      <FilterDropdown
        renderButton={({ open }) => (
          <>
            <FilterIcon />
            <p className="text-black500 whitespace-nowrap px-1.5">{t('templates.table.filters.title')}</p>
            {open ? (
              <ChevronUpIcon color={colors.black700} className="w-8 h-4" />
            ) : (
              <ChevronDownIcon color={colors.black700} className="w-8 h-4" />
            )}
          </>
        )}
        optionGroups={[
          {
            id: uuidv4(),
            label: t('templates.table.filters.tag'),
            options:
              tags?.map(tag => ({
                id: tag.tagName,
                value: tag.tagName,
              })) ?? [],
            onSetNewOption: handleChangeTagFilters,
            activeOptionIds: [...tagFilters],
          },
          {
            id: uuidv4(),
            label: t('templates.table.filters.gameCharacter'),
            options: gameCharactersOptions.map(option => ({
              id: option.code,
              value: option.value,
            })),
            onSetNewOption: option => setGameCharacterFilter(option?.id ?? ''),
            activeOptionIds: [gameCharacterFilter],
          },
          {
            id: uuidv4(),
            label: t('templates.table.filters.city'),
            options: citiesOptions.map(city => ({
              id: city,
              value: city,
            })),
            onSetNewOption: option => setCityFilter(option?.value ?? ''),
            activeOptionIds: [cityFilter],
          },
          ...(isGlobalAdmin
            ? [
                {
                  id: uuidv4(),
                  label: t('templates.table.filters.owner'),
                  options:
                    ownersOptions?.map(owner => ({
                      id: owner.id,
                      value: owner.email,
                    })) ?? [],
                  onSetNewOption: (option: DropdownOption | null) => setOwnerFilter(option?.value ?? ''),
                  activeOptionIds: !!ownersOptions.find(option => option.email === ownerFilter)?.id
                    ? [ownersOptions.find(option => option.email === ownerFilter)!.id]
                    : [],
                },
              ]
            : []),
          {
            id: uuidv4(),
            label: t('templates.table.filters.gameType'),
            options:
              gameTypes?.map(gameType => ({
                id: gameType.id,
                value: gameType.value,
                code: gameType.code,
              })) ?? [],
            onSetNewOption: option => setGameTypeFilter(option?.code ?? ''),
            activeOptionIds: [gameTypeFilter],
          },
        ]}
      />

      {!!gameCharacterFilter && <FilterChip label={gameCharacterLabel} onDelete={() => setGameCharacterFilter('')} />}
      {tagFilters.length > 0 &&
        tagFilters.map(singleTag => (
          <FilterChip key={singleTag} label={singleTag} onDelete={() => handleDeleteTagFilter(singleTag)} />
        ))}
      {!!cityFilter && <FilterChip label={cityFilter} onDelete={() => setCityFilter('')} />}
      {!!ownerFilter && <FilterChip label={ownerFilter} onDelete={() => setOwnerFilter('')} />}
      {!!gameTypeFilter && <FilterChip label={gameTypeLabel} onDelete={() => setGameTypeFilter('')} />}
    </div>
  );
};

export default TemplatesViewFilters;
